/**
 * Mediabunny always asks for `bytes=N-` and aborts the stream once it has
 * enough. On a small file that costs nothing; on a 60 MB proxy behind a CDN
 * the abort arrives long after CloudFront has committed to pushing the rest,
 * so a clip that needs a few MB pulls the whole tail. Playing one ad built
 * from several trims of one source measured 163 MB of transfer for a 60 MB
 * file, in three overlapping full-tail reads.
 *
 * Giving every open-ended range an end bounds that waste. Mediabunny reads a
 * short response as a server capping ranges and resumes from where it stopped,
 * so nothing is lost but the excess.
 */

/**
 * Ceiling for a still: sized from what one frame consumes before aborting,
 * which across a page of real ads ran 0.24 MB to 1.53 MB. One request covers
 * the common case and the tail takes two, against a transfer a quarter the
 * size.
 */
export const STILL_RANGE_BYTES = 1024 * 1024;

/**
 * Ceiling for playback. Larger than a still's because playback reads the file
 * in order: here the cap buys round trips rather than avoiding waste, and the
 * decoder has to stay ahead of the playhead.
 */
export const PLAYBACK_RANGE_BYTES = 4 * 1024 * 1024;

const OPEN_ENDED_RANGE = /^bytes=(\d+)-$/;

/**
 * The byte window to request for an open-ended range, or null to send the
 * header untouched.
 *
 * Capping is only safe once the file size is known. A reader that cannot read
 * `Content-Range` deduces the size from `Content-Length`, and a capped
 * response makes that deduction short — see `contentRangeFor`.
 */
export function cappedRange(
  range: string | null,
  fileSize: number | null,
  cap = STILL_RANGE_BYTES,
): { start: number; end: number } | null {
  const match = range === null ? null : OPEN_ENDED_RANGE.exec(range);
  if (!match || fileSize === null) return null;

  const start = Number(match[1]);
  const end = Math.min(start + cap - 1, fileSize - 1);
  return end <= start ? null : { start, end };
}

/** The `Content-Range` a capped response should report. */
export function contentRangeFor(
  window: { start: number; end: number },
  fileSize: number,
): string {
  return `bytes ${window.start}-${window.end}/${fileSize}`;
}

/**
 * Total size per source URL. Read from a HEAD, because the browser cannot read
 * `Content-Range` off these responses: the CDN sends `Access-Control-Allow-Origin`
 * without `Access-Control-Expose-Headers`, and `Content-Range` is not a
 * CORS-safelisted response header. `Content-Length` is safelisted, so a HEAD
 * gives the real total. Null when the probe cannot answer, which turns capping
 * off rather than guessing.
 */
const fileSizeBySrc = new Map<string, Promise<number | null>>();

function getFileSize(
  base: typeof fetch,
  src: string,
  credentials: RequestCredentials | undefined,
): Promise<number | null> {
  const existing = fileSizeBySrc.get(src);
  if (existing !== undefined) return existing;

  const promise = base(src, { method: "HEAD", credentials })
    .then((response) => {
      const length = response.headers.get("Content-Length");
      const size = length === null ? Number.NaN : Number(length);
      return response.ok && Number.isFinite(size) && size > 0 ? size : null;
    })
    .catch(() => null);
  fileSizeBySrc.set(src, promise);
  return promise;
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof Request) return input.url;
  return String(input);
}

/**
 * A fetch that gives every open-ended range an end, and tells the reader the
 * real file size that the capped response would otherwise hide.
 */
export function boundedRangeFetch(
  base: typeof fetch,
  cap = STILL_RANGE_BYTES,
): typeof fetch {
  return async (input, init) => {
    const headers = new Headers(init?.headers);
    const range = headers.get("Range");
    if (range === null || !OPEN_ENDED_RANGE.test(range)) {
      return base(input, init);
    }

    const src = urlOf(input);
    const fileSize = await getFileSize(base, src, init?.credentials);
    const window = cappedRange(range, fileSize, cap);
    if (window === null || fileSize === null) return base(input, init);

    headers.set("Range", `bytes=${window.start}-${window.end}`);
    const response = await base(input, { ...init, headers });
    if (response.status !== 206 || !response.body) return response;

    // Rebuilt so the size survives: headers we construct are ours to read,
    // unlike the CORS-filtered ones on the response the CDN returned.
    const exposed = new Headers(response.headers);
    exposed.set("Content-Range", contentRangeFor(window, fileSize));
    return new Response(response.body, {
      status: response.status,
      statusText: response.statusText,
      headers: exposed,
    });
  };
}

let installed = false;

/**
 * Bounds open-ended ranges for media readers that own their own `UrlSource`
 * and take no fetch override.
 *
 * `@remotion/media`'s `<Video>` and `<Audio>` build the source themselves and
 * accept only `credentials` and `requestInit`, so the playback path cannot be
 * handed a `fetchFn` the way `extract-video-frame` hands one to its own
 * source. Patching the global is the remaining seam. Mediabunny resolves
 * `fetch` at call time, and only a media reader sends an open-ended `Range`,
 * so every other caller passes straight through.
 *
 * Call once per realm from an app entry point. The preview iframe is its own
 * realm and needs its own call.
 */
export function installBoundedMediaRangeFetch(): void {
  if (installed) return;
  installed = true;
  globalThis.fetch = boundedRangeFetch(
    globalThis.fetch.bind(globalThis),
    PLAYBACK_RANGE_BYTES,
  );
}
